// @ts-check
// Horário semanal da página de agendamento (contrato 10 de agendamento, seção 5.1): cada dia ligado ou não, com até
// quatro faixas de horário no fuso da página.
import { html } from 'htm/preact';
import { BOOKING_LIMITS } from '../../../../shared/booking/booking-values.js';
import { EDITOR_WEEKDAYS, nextRange } from '../../lib/booking-pages.js';
import { Button, IconButton } from '../../ui/button.js';
import { Toggle } from '../../ui/feedback.js';
import { t } from '../../i18n/index.js';

/**
 * @typedef {Record<string, Array<{ start: string, end: string }>>} WeeklyHours
 * @param {{ value: WeeklyHours, onChange: (value: WeeklyHours) => void, disabled?: boolean }} props
 */
export function WeeklyHoursEditor({ value, onChange, disabled = false }) {
  /** @param {string} day @param {Array<{ start: string, end: string }>} ranges */
  const setDay = (day, ranges) => {
    const next = { ...value };
    if (ranges.length === 0) delete next[day];
    else next[day] = ranges;
    onChange(next);
  };
  return html`<div class="booking-weekly-hours" role="group" aria-label=${t('bookingPages.editor.weeklyHours')}>
    ${EDITOR_WEEKDAYS.map((day) => {
      const ranges = value[day] ?? [];
      const dayLabel = t(`bookingPages.weekdays.${day}`);
      return html`<div key=${day} class="booking-weekly-row" data-open=${ranges.length > 0 ? 'true' : 'false'}>
        <div class="booking-weekly-day">
          <${Toggle} value=${ranges.length > 0} disabled=${disabled} ariaLabel=${t('bookingPages.editor.dayOpen', { day: dayLabel })}
            onChange=${(/** @type {boolean} */ isOpen) => setDay(day, isOpen ? [{ start: '09:00', end: '12:00' }] : [])} />
          <span>${dayLabel}</span>
        </div>
        <div class="booking-weekly-ranges">
          ${ranges.length === 0 ? html`<span class="input-hint">${t('bookingPages.editor.dayClosed')}</span>` : null}
          ${ranges.map((range, index) => html`<div key=${index} class="booking-weekly-range">
            <input class="text-input" type="time" step="900" value=${range.start} disabled=${disabled}
              aria-label=${t('bookingPages.editor.rangeStart', { day: dayLabel, position: index + 1 })}
              onChange=${(/** @type {Event} */ event) => setDay(day, ranges.map((item, position) => (position === index ? { ...item, start: /** @type {HTMLInputElement} */ (event.currentTarget).value } : item)))} />
            <span aria-hidden="true">${t('bookingPages.editor.rangeTo')}</span>
            <input class="text-input" type="time" step="900" value=${range.end} disabled=${disabled}
              aria-label=${t('bookingPages.editor.rangeEnd', { day: dayLabel, position: index + 1 })}
              onChange=${(/** @type {Event} */ event) => setDay(day, ranges.map((item, position) => (position === index ? { ...item, end: /** @type {HTMLInputElement} */ (event.currentTarget).value } : item)))} />
            <${IconButton} icon="IconX" variant="tertiary" size="small" disabled=${disabled} ariaLabel=${t('bookingPages.editor.removeRange')}
              onClick=${() => setDay(day, ranges.filter((_item, position) => position !== index))} />
          </div>`)}
          ${ranges.length > 0 && ranges.length < BOOKING_LIMITS.rangesPerDay
            ? html`<${Button} title=${t('bookingPages.editor.addRange')} icon="IconPlus" variant="tertiary" size="small" disabled=${disabled}
                onClick=${() => setDay(day, [...ranges, nextRange(ranges)])} />`
            : null}
        </div>
      </div>`;
    })}
  </div>`;
}
